import React, { useMemo } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	Grid,
	Box,
	Stack,
	Typography,
	Chip,
	TextField,
	Button,
	CircularProgress,
	Paper,
} from "@mui/material";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../theme";

const ModalIntercambio = ({
	open,
	onClose,
	onConfirm,
	onPreview,
	firstUser,
	secondUser,
	firstUserId,
	secondUserId,
	swapStartAt,
	swapEndAt,
	swapReason,
	onChangeStartAt,
	onChangeEndAt,
	onChangeReason,
	swapLoading,
	previewGenerating,
}) => {
	const theme = useTheme();
	const colors = tokens(theme.palette.mode);
	const trimmedReason = useMemo(() => swapReason.trim(), [swapReason]);
	const disableFields = swapLoading || previewGenerating;

	const renderSwapUserCard = (user, label) => (
		<Paper
			elevation={3}
			sx={{
				p: 2,
				borderRadius: 2,
				minHeight: 150,
				width: 400,
				backgroundColor: colors.primary[400],
				border: `1px solid ${colors.blueAccent[600]}`,
				display: "flex",
				flexDirection: "column",
				gap: 0.5,
			}}
		>
			<Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
				<Typography variant="subtitle2" color={colors.grey[100]}>
					{label}
				</Typography>
				<Chip
					label={`ID ${user?.id ?? "-"}`}
					size="small"
					sx={{
						bgcolor: colors.blueAccent[700],
						color: colors.grey[100],
					}}
				/>
			</Stack>
			<Typography variant="h6" color={colors.grey[100]}>
				{user ? `${user.firstname} ${user.lastname}` : "No disponible"}
			</Typography>
			<Typography variant="body2" color={colors.grey[300]}>
				Horario actual: {user?.horario_descripcion ?? "-"}
			</Typography>
			<Typography variant="body2" color={colors.grey[300]}>
				Grupo: {user?.grupo ?? "-"}
			</Typography>
		</Paper>
	);

	return (
		<Dialog open={open} onClose={onClose}>
			<DialogTitle sx={{ backgroundColor: colors.primary[600], color: colors.grey[100] }}>
				Confirmar intercambio
			</DialogTitle>
			<DialogContent
				dividers
				sx={{
					backgroundColor: colors.primary[500],
					width: 460,
				}}
			>
				<Stack spacing={2}>
					<DialogContentText sx={{ color: colors.grey[200] }}>
						{`Se intercambiarán los horarios seleccionados entre los usuarios ${firstUserId ?? "-"} y ${secondUserId ?? "-"}.`}
					</DialogContentText>
					<Grid container spacing={3} alignItems="center" justifyContent="center">
						<Grid item xs={12} md={5}>
							{renderSwapUserCard(firstUser, "Usuario A")}
						</Grid>
						<Grid item xs={12} md={2} sx={{ display: "flex", justifyContent: "center" }}>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									width: 50,
									height: 50,
									borderRadius: "50%",
									backgroundColor: colors.primary[400],
									border: `2px solid ${colors.blueAccent[600]}`,
								}}
							>
								<CompareArrowsIcon
									sx={{
										fontSize: 36,
										color: colors.blueAccent[300],
										transform: "rotate(90deg)",
									}}
								/>
							</Box>
						</Grid>
						<Grid item xs={12} md={5}>
							{renderSwapUserCard(secondUser, "Usuario B")}
						</Grid>
					</Grid>
					<Grid container spacing={2}>
						<Grid item xs={12} md={6} mt={2}>
							<TextField
								label="Fecha y hora de inicio"
								type="datetime-local"
								value={swapStartAt}
								onChange={(event) => onChangeStartAt(event.target.value)}
								InputLabelProps={{ shrink: true }}
								disabled={disableFields}
								fullWidth
							/>
						</Grid>
						<Grid item xs={12} md={6} ml={4} mt={2}>
							<TextField
								label="Fecha y hora de fin"
								type="datetime-local"
								value={swapEndAt}
								onChange={(event) => onChangeEndAt(event.target.value)}
								InputLabelProps={{ shrink: true }}
								disabled={disableFields}
								fullWidth
							/>
						</Grid>
						<Box
							mb={4}
							width="100%"
							sx={{
								borderRadius: "2%",
								display: "flex",
								alignItems: "center",
								gap: 1,
							}}
						>
							<TextField
								label="Motivo del intercambio"
								multiline
								minRows={3}
								value={swapReason}
								onChange={(event) => onChangeReason(event.target.value)}
								disabled={disableFields}
								inputProps={{ maxLength: 300 }}
								fullWidth
								helperText="Describe brevemente la razón del intercambio"
								sx={{
									width: 405,
									height: "100%",
									mt: 1,
								}}
							/>
						</Box>
					</Grid>
				</Stack>
			</DialogContent>
			<DialogActions sx={{ backgroundColor: colors.primary[600] }}>
				<Button
					onClick={onPreview}
					color="secondary"
					variant="outlined"
					disabled={previewGenerating || swapLoading || !trimmedReason}
					sx={{
						borderColor: colors.blueAccent[300],
						color: colors.grey[100],
						minWidth: "130px",
						"&:hover": {
							borderColor: colors.blueAccent[100],
							backgroundColor: colors.primary[500],
						},
					}}
				>
					{previewGenerating ? (
						<CircularProgress size={20} sx={{ color: "inherit" }} />
					) : (
						"Vista previa"
					)}
				</Button>
				<Button
					color="primary"
					sx={{
						backgroundColor: "red",
						border: "none",
						color: "#ffffff",
						minWidth: "100px",
						cursor: "pointer",
						"&:hover": { backgroundColor: "#910707" },
					}}
					onClick={onClose}
					disabled={swapLoading}
				>
					Cancelar
				</Button>
				
				<Button
					onClick={onConfirm}
					color="primary"
					variant="contained"
					disabled={swapLoading || previewGenerating || !trimmedReason}
					sx={{
						backgroundColor: "rgb(206, 220, 0)",
						border: "none",
						color: "black",
						minWidth: "100px",
						cursor: "pointer",
						"&:hover": { backgroundColor: "#5b6101" },
					}}
				>
					{swapLoading ? <CircularProgress size={20} sx={{ color: "inherit" }} /> : "Confirmar"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ModalIntercambio;
